/**
 * User dashboard with recent configurations and quick actions
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageTransition } from '@/components/layout/PageTransition';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, 
  TrendingUp, 
  Clock, 
  Plus, 
  ArrowRight,
  Settings,
  DollarSign,
  Rocket,
  FileText
} from 'lucide-react';
import { useWizardPersistence } from '@/hooks/useWizardPersistence';
import { format } from 'date-fns';

export default function Dashboard() {
  const navigate = useNavigate();
  const { savedScenarios, loadScenariosList } = useWizardPersistence();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadScenariosList().finally(() => setIsLoading(false));
  }, [loadScenariosList]);

  const quickActions = [
    {
      icon: Sparkles,
      title: 'New Configuration',
      description: 'Start the wizard to configure AI infrastructure',
      href: '/wizard',
      color: 'bg-violet-500',
    },
    {
      icon: TrendingUp,
      title: 'Infrastructure Sizing',
      description: 'Calculate hardware requirements',
      href: '/sizing',
      color: 'bg-blue-500',
    },
    {
      icon: DollarSign,
      title: 'Cost Analysis',
      description: 'Compare deployment costs',
      href: '/cost-simulator',
      color: 'bg-green-500',
    },
    {
      icon: Rocket,
      title: 'Live Demos',
      description: 'Try AI models in real-time',
      href: '/demos',
      color: 'bg-orange-500',
    },
  ];

  const stats = [
    {
      label: 'Saved Configurations',
      value: savedScenarios.length,
      icon: Settings,
      color: 'text-blue-600',
    },
    {
      label: 'Quick Actions',
      value: quickActions.length,
      icon: Sparkles,
      color: 'text-violet-600',
    },
  ];

  return (
    <MainLayout>
      <PageTransition>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Breadcrumbs />
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your AI infrastructure configurations and access tools
            </p>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {stats.map((stat) => (
              <Card key={stat.label}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <stat.icon className={`h-12 w-12 ${stat.color}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Quick Actions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickActions.map((action) => (
                <Card 
                  key={action.title}
                  className="group cursor-pointer hover:shadow-elegant transition-smooth"
                  onClick={() => navigate(action.href)}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`p-3 rounded-lg ${action.color} text-white`}>
                        <action.icon className="h-6 w-6" />
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                    <CardTitle className="text-base">{action.title}</CardTitle>
                    <CardDescription className="text-xs">
                      {action.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>

          {/* Recent Configurations */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold">Recent Configurations</h2>
              <Button variant="outline" size="sm" onClick={() => navigate('/wizard')}>
                <Plus className="h-4 w-4 mr-2" />
                New Configuration
              </Button>
            </div>

            {isLoading ? (
              <Card>
                <CardContent className="p-12">
                  <div className="flex items-center justify-center text-muted-foreground">
                    <Clock className="h-5 w-5 animate-spin mr-2" />
                    Loading configurations...
                  </div>
                </CardContent>
              </Card>
            ) : savedScenarios.length === 0 ? (
              <Card>
                <CardContent className="p-12">
                  <div className="text-center space-y-4">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="text-lg font-medium mb-1">No configurations yet</p>
                      <p className="text-sm text-muted-foreground">
                        Start by creating your first configuration in the wizard
                      </p>
                    </div>
                    <Button onClick={() => navigate('/wizard')}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Start Configuration Wizard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedScenarios.slice(0, 6).map((scenario) => (
                  <Card 
                    key={scenario.id}
                    className="cursor-pointer hover:shadow-elegant transition-smooth"
                    onClick={() => navigate(`/wizard?scenario=${scenario.id}`)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <CardTitle className="text-base line-clamp-1">
                          {scenario.meta?.name || 'Untitled Configuration'}
                        </CardTitle>
                        <Badge variant="secondary" className="text-xs">
                          {Array.isArray((scenario as any).personas) ? (scenario as any).personas.length : 0} personas
                        </Badge>
                      </div>
                      <CardDescription className="text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {scenario.updated_at && format(new Date(scenario.updated_at), 'MMM d, yyyy')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Settings className="h-3 w-3" />
                        {(scenario as any).model_selections?.selectedModelIds?.length || 0} models selected
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </PageTransition>
    </MainLayout>
  );
}
